import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  trackGoogleAnalyticsPageView,
  trackGoogleAnalyticsTimeSpent
} from 'shared/hooks/analytics/service-handlers/useGoogleAnalytics'
import {
  type AnalyticsMeasurementCheckpoint,
  AnalyticsTrackingDB,
  type AnalyticsUsageDay
} from 'shared/services/analytics/AnalyticsTrackingDB'

import {
  ANALYTICS_CHECKPOINT_INTERVAL_MS,
  ANALYTICS_CHECKPOINT_RETENTION_MS,
  ANALYTICS_MEASUREMENT_CHECKPOINT_SESSION_KEY,
  ANALYTICS_MEASUREMENT_ID_SESSION_KEY,
  MIN_TRACKED_TIME_SECONDS
} from '../AnalyticsTracker.constants'
import {
  advanceTiming,
  createTimingState,
  isReportableUsageDay,
  readAnalyticsClock,
  restoreTimingDays,
  takeCompletedDays
} from '../AnalyticsTracker.timing'
import type {
  AnalyticsMeasurement,
  AnalyticsTrackingView
} from '../AnalyticsTracker.types'
import type { AnalyticsMeasurementTrackerProps } from './AnalyticsMeasurementTracker.types'
import {
  canRecoverCurrentDocumentMeasurement,
  canRestoreCheckpoint,
  createMeasurementId,
  getSharedTrackingDB,
  isDocumentForeground,
  msUntilNextUtcDay,
  parseCheckpoint,
  readSessionStorage,
  removeSessionStorage,
  writeSessionStorage
} from './AnalyticsMeasurementTracker.utils'
import { useMeasurementLifecycle } from './useMeasurementLifecycle'

export const AnalyticsMeasurementTracker = ({
  enabled = true,
  view,
  checkpointScope = '',
  foreground = isDocumentForeground,
  activityVersion = 0,
  viewVersion = 0
}: AnalyticsMeasurementTrackerProps) => {
  const {
    path: pathname = '',
    appId = '',
    appName = '',
    appPath = '',
    userId,
    organizationId,
    originalOrganizationId,
    pageLocation,
    pageReferrer,
    trackingSource
  } = view ?? {}
  const canTrackCurrentPage = view !== null
  // Callers create view objects inline. Depend on fields so an unrelated
  // render does not end the measurement or emit another page view.
  const trackingView = useMemo<AnalyticsTrackingView | null>(
    () =>
      canTrackCurrentPage
        ? {
            path: pathname,
            title: appName,
            appId,
            appName,
            appPath,
            userId,
            organizationId,
            originalOrganizationId,
            pageLocation,
            pageReferrer,
            trackingSource
          }
        : null,
    [
      canTrackCurrentPage,
      pathname,
      appId,
      appName,
      appPath,
      userId,
      organizationId,
      originalOrganizationId,
      pageLocation,
      pageReferrer,
      trackingSource
    ]
  )
  const idSessionKey = ANALYTICS_MEASUREMENT_ID_SESSION_KEY + checkpointScope
  const checkpointSessionKey =
    ANALYTICS_MEASUREMENT_CHECKPOINT_SESSION_KEY + checkpointScope
  const foregroundRef = useRef(foreground)
  foregroundRef.current = foreground
  const viewVersionRef = useRef(viewVersion)
  viewVersionRef.current = viewVersion
  const lastPageViewRef = useRef<string | null>(null)

  const measurementRef = useRef<AnalyticsMeasurement | null>(null)
  const enabledRef = useRef(enabled)
  const frozenRef = useRef(false)
  const intervalRef = useRef<number | undefined>(undefined)
  const midnightRef = useRef<number | undefined>(undefined)
  enabledRef.current = enabled

  const stopTimers = useCallback(() => {
    window.clearInterval(intervalRef.current)
    window.clearTimeout(midnightRef.current)
    intervalRef.current = undefined
    midnightRef.current = undefined
  }, [])

  const clearPersistedMeasurement = useCallback(
    (id: string) => {
      if (readSessionStorage(idSessionKey) === id) {
        removeSessionStorage(idSessionKey)
        removeSessionStorage(checkpointSessionKey)
      }
      getSharedTrackingDB()
        .remove(id)
        .catch(error => AnalyticsTrackingDB.devWarn('remove checkpoint', error))
    },
    [idSessionKey, checkpointSessionKey]
  )

  const reportDays = useCallback(
    (measurement: AnalyticsMeasurement, days: AnalyticsUsageDay[]) => {
      const now = measurement.timing.lastClock.wallTimeMs
      for (const day of days) {
        if (!isReportableUsageDay(day.usageDay, now)) continue
        const timeSpentSeconds = Math.round(day.elapsedTimeMs / 1000)
        if (timeSpentSeconds <= MIN_TRACKED_TIME_SECONDS) continue
        trackGoogleAnalyticsTimeSpent({
          ...measurement.context,
          timeSpentSeconds,
          foregroundTimeSpent: Math.min(
            timeSpentSeconds,
            Math.round(day.foregroundTimeMs / 1000)
          ),
          measurementId: day.measurementId,
          usageDay: day.usageDay
        })
      }
    },
    []
  )

  // All finalization, persistence and lifecycle paths consume the same clock
  // interval. No caller may reintroduce a long `now - foregroundStart` tail.
  const sampleCurrentMeasurement = useCallback(
    (acceptForegroundTail = true) => {
      const measurement = measurementRef.current
      if (!enabledRef.current || !measurement) return
      advanceTiming(
        measurement.timing,
        readAnalyticsClock(),
        !frozenRef.current && foregroundRef.current(),
        createMeasurementId,
        acceptForegroundTail
      )
      if (!measurement.restoring) {
        reportDays(measurement, takeCompletedDays(measurement.timing))
      }
    },
    [reportDays]
  )

  const persistCurrentCheckpoint = useCallback(
    (acceptForegroundTail = true) => {
      sampleCurrentMeasurement(acceptForegroundTail)
      const measurement = measurementRef.current
      if (!enabledRef.current || !measurement || measurement.restoring) return
      const checkpoint: AnalyticsMeasurementCheckpoint = {
        ...measurement.context,
        version: 2,
        measurementId: measurement.id,
        createdAt: measurement.createdAt,
        updatedAt: measurement.timing.lastClock.wallTimeMs,
        days: measurement.timing.days.map(day => ({ ...day }))
      }
      writeSessionStorage(idSessionKey, checkpoint.measurementId)
      writeSessionStorage(checkpointSessionKey, JSON.stringify(checkpoint))
      getSharedTrackingDB()
        .write(checkpoint)
        .catch(error => AnalyticsTrackingDB.devWarn('write checkpoint', error))
    },
    [sampleCurrentMeasurement, idSessionKey, checkpointSessionKey]
  )

  const syncTimers = useCallback(() => {
    if (!enabledRef.current || !measurementRef.current || frozenRef.current) {
      stopTimers()
      return
    }
    if (document.visibilityState !== 'visible') {
      window.clearInterval(intervalRef.current)
      intervalRef.current = undefined
    } else if (intervalRef.current === undefined) {
      intervalRef.current = window.setInterval(() => {
        const foreground = !frozenRef.current && foregroundRef.current()
        // Focus can return inside an iframe without a window focus event.
        // Keep probing visible documents, but only persist periodic samples
        // while foreground. Unfocused samples reset the timing baseline.
        if (foreground) persistCurrentCheckpoint()
        else sampleCurrentMeasurement(false)
        if (document.visibilityState !== 'visible' || frozenRef.current) {
          window.clearInterval(intervalRef.current)
          intervalRef.current = undefined
        }
      }, ANALYTICS_CHECKPOINT_INTERVAL_MS)
    }
    // Keep the existing once-per-day boundary callback in background tabs.
    // This can submit already-recorded foreground before it expires, while
    // hidden-document sampling and repeated storage writes remain paused.
    if (midnightRef.current === undefined) {
      const onMidnight = () => {
        midnightRef.current = undefined
        const foreground = !frozenRef.current && foregroundRef.current()
        persistCurrentCheckpoint(foreground)
        if (
          enabledRef.current &&
          measurementRef.current &&
          !frozenRef.current
        ) {
          midnightRef.current = window.setTimeout(
            onMidnight,
            msUntilNextUtcDay()
          )
        } else {
          stopTimers()
        }
      }
      midnightRef.current = window.setTimeout(onMidnight, msUntilNextUtcDay())
    }
  }, [persistCurrentCheckpoint, sampleCurrentMeasurement, stopTimers])

  const discardCurrentMeasurement = useCallback(() => {
    const measurement = measurementRef.current
    measurementRef.current = null
    stopTimers()
    if (!measurement) return
    clearPersistedMeasurement(measurement.id)
    if (measurement.recoveryId && measurement.recoveryId !== measurement.id) {
      clearPersistedMeasurement(measurement.recoveryId)
    }
  }, [clearPersistedMeasurement, stopTimers])

  const sendTimeSpent = useCallback(() => {
    if (!enabledRef.current || !measurementRef.current) return
    sampleCurrentMeasurement()
    const measurement = measurementRef.current
    // Clear first, so pagehide + React cleanup cannot send twice.
    measurementRef.current = null
    stopTimers()
    clearPersistedMeasurement(measurement.id)
    if (measurement.recoveryId && measurement.recoveryId !== measurement.id) {
      clearPersistedMeasurement(measurement.recoveryId)
    }
    reportDays(measurement, measurement.timing.days)
  }, [
    clearPersistedMeasurement,
    reportDays,
    sampleCurrentMeasurement,
    stopTimers
  ])

  const beginTrackingView = useCallback(
    (params: AnalyticsTrackingView) => {
      if (!enabledRef.current) return
      const clock = readAnalyticsClock()
      const canRecover = canRecoverCurrentDocumentMeasurement()
      const storedId = canRecover ? readSessionStorage(idSessionKey) : null
      const serialized = canRecover
        ? readSessionStorage(checkpointSessionKey)
        : null
      const parsed = serialized ? parseCheckpoint(serialized) : undefined
      const checkpoint =
        storedId &&
        canRestoreCheckpoint(parsed, storedId, params, clock.wallTimeMs)
          ? parsed
          : undefined
      // A present but invalid/legacy checkpoint must not fall back to an older
      // IndexedDB copy. Its unsegmented foreground total cannot be repaired.
      if (storedId && serialized && !checkpoint)
        clearPersistedMeasurement(storedId)
      const recoveryId = storedId && !serialized ? storedId : undefined
      const id = checkpoint?.measurementId ?? createMeasurementId()
      const measurement: AnalyticsMeasurement = {
        id,
        context: params,
        createdAt: checkpoint?.createdAt ?? clock.wallTimeMs,
        timing: createTimingState(
          clock,
          !frozenRef.current && foregroundRef.current(),
          checkpoint ? createMeasurementId() : id
        ),
        restoring: Boolean(recoveryId),
        recoveryId
      }
      if (checkpoint) restoreTimingDays(measurement.timing, checkpoint)
      measurementRef.current = measurement
      const pageViewKey = JSON.stringify([
        params.path,
        params.appId,
        params.userId,
        params.organizationId,
        params.originalOrganizationId,
        viewVersionRef.current
      ])
      // StrictMode effect replay retains refs but recreates the measurement.
      // Deduplicate that replay only; navigation revisions preserve real revisits.
      if (lastPageViewRef.current !== pageViewKey) {
        trackGoogleAnalyticsPageView(params)
        lastPageViewRef.current = pageViewKey
      }
      persistCurrentCheckpoint()
      syncTimers()
      if (!recoveryId) return

      getSharedTrackingDB()
        .read(recoveryId)
        .then(recovered => {
          if (measurementRef.current !== measurement || !enabledRef.current)
            return
          sampleCurrentMeasurement()
          if (
            canRestoreCheckpoint(
              recovered,
              recoveryId,
              params,
              clock.wallTimeMs
            )
          ) {
            measurement.id = recovered.measurementId
            measurement.createdAt = recovered.createdAt
            restoreTimingDays(measurement.timing, recovered)
          } else {
            clearPersistedMeasurement(recoveryId)
          }
          measurement.restoring = false
          persistCurrentCheckpoint()
          syncTimers()
        })
        .catch(error => {
          AnalyticsTrackingDB.devWarn('restore checkpoint', error)
          if (measurementRef.current !== measurement || !enabledRef.current)
            return
          clearPersistedMeasurement(recoveryId)
          measurement.restoring = false
          persistCurrentCheckpoint()
          syncTimers()
        })
    },
    [
      clearPersistedMeasurement,
      persistCurrentCheckpoint,
      sampleCurrentMeasurement,
      syncTimers,
      idSessionKey,
      checkpointSessionKey
    ]
  )

  useEffect(() => {
    if (!enabled) return
    getSharedTrackingDB()
      .prune(ANALYTICS_CHECKPOINT_RETENTION_MS)
      .catch(error => AnalyticsTrackingDB.devWarn('prune checkpoints', error))
  }, [enabled])

  useEffect(() => {
    if (!enabled || !trackingView) {
      lastPageViewRef.current = null
      if (!enabled) frozenRef.current = false
      discardCurrentMeasurement()
      return
    }
    beginTrackingView(trackingView)
    return sendTimeSpent
  }, [
    enabled,
    trackingView,
    viewVersion,
    beginTrackingView,
    discardCurrentMeasurement,
    sendTimeSpent
  ])

  useEffect(() => {
    if (!enabled) return
    const measurement = measurementRef.current
    // Frames get an activity signal for every focus change in the shell. Only
    // sample when this view's own foreground state changed.
    if (
      measurement &&
      measurement.timing.foreground ===
        (!frozenRef.current && foregroundRef.current())
    )
      return
    persistCurrentCheckpoint()
    syncTimers()
  }, [activityVersion, enabled, persistCurrentCheckpoint, syncTimers])

  const restoreTrackingView = useCallback(() => {
    if (measurementRef.current || !trackingView) return
    frozenRef.current = false
    lastPageViewRef.current = null
    beginTrackingView(trackingView)
  }, [trackingView, beginTrackingView])

  useMeasurementLifecycle({
    enabled,
    frozenRef,
    persistCurrentCheckpoint,
    sendTimeSpent,
    stopTimers,
    syncTimers,
    restoreTrackingView
  })

  return null
}
